import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, Sparkles, Wallet, Globe2, Receipt, BarChart3, Check,
  ShieldCheck, Code2, UserCheck, LayoutTemplate, BookOpen, Zap,
  Building2, TrendingUp, Scale, X, Repeat, ArrowUp,
} from "lucide-react";
import Logo from "@/components/Logo";
import { LOGO_URL } from "@/lib/api";
import VerbRail from "@/components/VerbRail";

export default function Landing() {
  const [showBackToOverview, setShowBackToOverview] = useState(false);

  useEffect(() => {
    const handler = () => setShowBackToOverview(true);
    window.addEventListener("verb-jumped", handler);
    return () => window.removeEventListener("verb-jumped", handler);
  }, []);

  // Auto-dismiss once the user scrolls back to the overview section.
  // Delay listener attachment so the smooth-scroll AWAY from #narrative
  // doesn't dismiss the chip while narrative is still in view.
  useEffect(() => {
    if (!showBackToOverview) return;
    let onScroll = null;
    const timer = setTimeout(() => {
      onScroll = () => {
        const el = document.querySelector("#narrative");
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.5 && rect.bottom > 0) {
          setShowBackToOverview(false);
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
    }, 1500);
    return () => {
      clearTimeout(timer);
      if (onScroll) window.removeEventListener("scroll", onScroll);
    };
  }, [showBackToOverview]);

  return (
    <div className="min-h-screen bg-white">
      <VerbRail />
      {/* Nav */}
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-12">
        <Logo />
        <nav className="hidden items-center gap-8 text-sm text-muted-foreground md:flex">
          <a href="#features" className="hover:text-foreground">Features</a>
          <a href="#ai" className="hover:text-foreground">CFO's Dashboard</a>
          <Link to="/learn" className="hover:text-foreground" data-testid="nav-learn-marketing">Learn</Link>
          <Link to="/pricing" className="hover:text-foreground" data-testid="nav-pricing">Pricing</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="hidden text-sm text-muted-foreground hover:text-foreground md:block">Sign in</Link>
          <Link
            to="/signup"
            data-testid="nav-signup"
            className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-transform hover:bg-foreground/90 active:scale-95"
          >
            Start free <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative mx-auto max-w-7xl px-6 pb-28 pt-12 md:px-12 md:pb-40 md:pt-20">
        <div className="grid grid-cols-1 items-end gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-border bg-white px-3.5 py-1.5 text-xs font-medium text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
              New — Sri × Lemonade integration · Bill scanning · Vendors directory
            </div>
            <h1 className="font-display text-[3.3rem] font-normal leading-[0.95] tracking-tight text-foreground md:text-[5.5rem]">
              Your
              <br />
              <span className="italic text-foreground/80">CFO's Dashboard.</span>{/* Chief Financial Officer */}
            </h1>
            <p className="mt-8 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Financial intelligence for companies that want to understand where their money is going — and what to do next. Multi‑currency books, live cash‑flow forecast, and answers grounded in your real numbers.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/signup"
                data-testid="hero-cta-signup"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-medium text-primary-foreground transition-transform hover:bg-primary/90 active:scale-95"
              >
                Start Free <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login?demo=1"
                data-testid="hero-cta-try-demo"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-7 py-4 text-base font-medium hover:bg-accent"
              >
                <Sparkles className="h-4 w-4" /> Try the demo
              </Link>
              <Link
                to="/pricing"
                data-testid="hero-cta-pricing"
                className="text-base font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                5 seats free · then CHF 20/seat →
              </Link>
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.22em] text-muted-foreground">
              5 seats free · CHF 20 per additional seat / month · Cancel anytime
            </p>
          </div>

          {/* Visual column */}
          <div className="relative lg:col-span-5">
            <div className="relative overflow-hidden rounded-[32px] bg-primary p-10 shadow-[0_40px_80px_-20px_rgba(240,196,25,0.55)]">
              <div className="grain" aria-hidden></div>
              <img src={LOGO_URL} alt="" className="h-24 w-24 rounded-[22%]" />
              <div className="mt-8 font-display text-3xl leading-tight tracking-tight text-foreground">
                Runway
              </div>
              <div className="mt-1 font-display text-[5rem] leading-none text-foreground">
                18<span className="text-foreground/50">mo</span>
              </div>
              <div className="mt-4 text-sm text-foreground/70">Cash CHF 108,400 · Burn CHF 6,022/mo</div>
              <div className="mt-10 rounded-2xl bg-white/90 p-4">
                <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  AI CFO · just now
                </div>
                <div className="mt-2 text-sm text-foreground">
                  &ldquo;You can hire one senior engineer at CHF 9k/mo and keep 12 months runway.&rdquo;
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KNOW → UNDERSTAND → FORECAST → DECIDE → ACT — the 5-verb narrative */}
      <section id="narrative" className="border-y border-border bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-20">
          <div className="max-w-2xl">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">How Lemonade works</p>
            <h2 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
              Five verbs.<br /><span className="italic text-foreground/70">Every financial decision.</span>
            </h2>
            <p className="mt-4 max-w-lg text-sm text-muted-foreground">
              Every screen in Lemonade maps to one of five moves — the same shape every good CFO uses.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
            <Verb n="01" verb="Know" copy="Every number, sourced. Every transaction, categorised. Nothing invented." tag="Books · Ledger" anchor="#features" />
            <Verb n="02" verb="Understand" copy="Ask why, not just what. AI CFO answers grounded in your live books." tag="CFO's Dashboard · Learn" anchor="#ai" />
            <Verb n="03" verb="Forecast" copy="Base · Upside · Downside. Cash 90 days out. Runway that stays honest." tag="Forecast · Scenarios" anchor="#forecast" />
            <Verb n="04" verb="Decide" copy="Signals ranked by materiality. Playbooks, not panic. Decisions on record." tag="Signals · Board Pack" anchor="#decide" />
            <Verb n="05" verb="Act" copy="Send the invoice. Renegotiate the vendor. Cut the license. In the same tab." tag="Invoicing · Vendors · Licenses" anchor="#act" />
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section id="features" className="mx-auto max-w-7xl px-6 pt-24 pb-24 md:px-12">
        <div className="mb-14 max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
            What&rsquo;s inside
          </p>
          <h2 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
            A financial OS, not another spreadsheet.
          </h2>
          <p className="mt-4 max-w-lg text-sm text-muted-foreground">
            Everything you need to run a global business end-to-end — from the first invoice to year-end filings — held together by an AI CFO that actually reads your books.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-6">
          <FeatureCard
            className="md:col-span-4 bg-primary"
            iconClass="bg-foreground text-primary"
            icon={Sparkles}
            eyebrow="Understand · CFO's Dashboard"
            title="Ask Lemonade anything."
            desc="Weekly pulse emails plus live Q&amp;A — grounded in your real numbers, never hallucinated."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={Receipt}
            eyebrow="Act · Invoicing"
            title="Send in 30 seconds."
            desc="Scan, paste an email, pick a template, or type. Stripe paid-links built-in."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={TrendingUp}
            eyebrow="Forecast · Cash"
            title="Cash 90 days out."
            desc="Daily projection from recurring, receivables &amp; trailing spend. See the dip before it hits."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={Scale}
            eyebrow="Know · Tax &amp; Ledger"
            title="Swiss MwSt · EU VIES · US sales tax."
            desc="Quarterly tax packet + double-entry ledger + year-end ZIP for your accountant."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={Building2}
            eyebrow="Know · Banking"
            title="Bank-feed auto-match."
            desc="Upload CSV, we reconcile against invoices &amp; expenses automatically."
          />
          <FeatureCard
            className="md:col-span-3"
            icon={ShieldCheck}
            eyebrow="Decide · Workspaces"
            title="Team with roles &amp; approvals."
            desc="Owner · admin · member · viewer. Large invoices need admin sign-off. Invite your accountant as a viewer."
          />
          <FeatureCard
            className="md:col-span-3 bg-foreground text-background"
            iconClass="bg-primary text-foreground"
            icon={Code2}
            eyebrow="Platform"
            title="Public API &amp; webhooks."
            desc="lmn_live_ keys for Zapier &amp; custom code. HMAC-signed webhooks on every important event."
            dark
          />
          <FeatureCard
            className="md:col-span-2"
            icon={BookOpen}
            eyebrow="Docs Q&amp;A"
            title="Ask your contract."
            desc="Upload a PDF, ask &lsquo;what&rsquo;s the late-fee clause&rsquo; — answers grounded in the doc text."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={Wallet}
            eyebrow="Budgets"
            title="Categories, capped."
            desc="Monthly ceilings with live variance bars. Meals over budget? You&rsquo;ll know today."
          />
          <FeatureCard
            className="md:col-span-2"
            icon={Globe2}
            eyebrow="Global"
            title="Multi‑currency native."
            desc="CHF, EUR, USD, INR, GBP. FX reconciled, gains/losses booked automatically."
          />
        </div>
      </section>

      {/* Sri × Lemonade integration */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12" data-testid="sri-integration-section">
        <div className="relative overflow-hidden rounded-[32px] border border-border bg-foreground p-10 text-background md:p-16">
          <div className="grain opacity-30" aria-hidden></div>
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.22em] text-background/70">
                <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                Now integrated
              </div>
              <h3 className="font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
                Sri &amp; Lemonade,
                <br />
                <span className="italic text-primary">one calm brain.</span>
              </h3>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-background/70">
                Sri, the AI CFO at <span className="font-medium text-background">sri.mahakalitribunal.com</span>, now reads your Lemonade books in real time. Cash, MRR, burn, receivables, payables — every answer Sri gives is grounded in your live financial-context feed, never fabricated.
              </p>
              <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  "Scoped read-only keys · one click from Settings",
                  "ETag revalidation · zero-cost polling",
                  "Reporting-currency safe · never invents FX",
                  "Auditable · every access logged",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5 text-sm text-background/85">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/signup"
                  data-testid="sri-cta-signup"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-foreground transition-transform hover:bg-primary/90 active:scale-95"
                >
                  Connect Sri to your books <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="https://sri.mahakalitribunal.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="sri-cta-visit"
                  className="text-sm font-medium text-background/70 underline-offset-4 hover:text-background hover:underline"
                >
                  Meet Sri →
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur">
                <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-background/50">
                  Sri → Lemonade · financial-context
                </div>
                <pre className="mt-4 overflow-x-auto rounded-2xl bg-black/40 p-5 font-mono text-[11px] leading-relaxed text-background/85">
{`GET /api/integration/sri/financial-context
Authorization: Bearer lmn_sri_••••

200 OK · ETag: "75590a…"
{
  "reporting_currency": "CHF",
  "financials": {
    "cash":              108400.00,
    "monthly_revenue":    24120.00,
    "mrr":                18500.00,
    "receivables":        14260.00,
    "payables":            1454.17,
    "revenue_growth_pct":     6.4,
    "min_operating_reserve": 17040.00
  }
}`}
                </pre>
                <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-background/50">
                  <span>Cached · 60s</span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />Live</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's new changelog */}
      <section id="forecast" className="border-y border-border bg-[hsl(0_0%_98.5%)] py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 flex items-end justify-between gap-6">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Shipped recently</p>
              <h3 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">What&rsquo;s new.</h3>
            </div>
            <Link to="/signup" className="hidden text-sm text-muted-foreground hover:text-foreground md:block">See it all inside →</Link>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {CHANGELOG.map((c) => (
              <div key={c.label} className="rounded-[24px] border border-border bg-white p-6">
                <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  <c.icon className="h-3.5 w-3.5" /> {c.sprint}
                </div>
                <div className="mt-2 font-display text-xl tracking-tight">{c.label}</div>
                <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />{it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Why Lemonade</p>
          <h3 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">Less stack. More answers.</h3>
          <p className="mt-4 max-w-lg text-sm text-muted-foreground">
            Most SMBs wire together 4+ tools: accounting software, an invoice sender, a forecast spreadsheet, a CFO consultant. Lemonade replaces all four — in one calm, beautiful place.
          </p>
        </div>
        <div className="overflow-x-auto rounded-[28px] border border-border bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-6 py-5 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Capability</th>
                <th className="px-6 py-5 text-xs font-semibold uppercase tracking-[0.18em] text-foreground">Lemonade</th>
                <th className="px-6 py-5 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">QuickBooks</th>
                <th className="px-6 py-5 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Xero</th>
                <th className="px-6 py-5 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Wave</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.label} className="border-b border-border last:border-0">
                  <td className="px-6 py-4 font-medium">{row.label}</td>
                  <Cell v={row.l} primary />
                  <Cell v={row.q} />
                  <Cell v={row.x} />
                  <Cell v={row.w} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* AI CFO strip */}
      <section id="ai" className="border-y border-border bg-[hsl(0_0%_98.5%)] py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 md:grid-cols-2 md:px-12">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">CFO's Dashboard</p>
            <h3 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
              A CFO in your pocket.
            </h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
              Lemonade&rsquo;s AI CFO is grounded in your real books. Ask anything — and get an answer rooted in your revenue, burn and receivables.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "What&rsquo;s my runway at the current burn?",
                "Which clients owe me the most?",
                "Where am I overspending this quarter?",
                "Can I afford to hire a designer at CHF 7k?",
              ].map((q) => (
                <li key={q} className="flex items-start gap-3 text-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"></span>
                  <span dangerouslySetInnerHTML={{ __html: q }} />
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-[32px] border border-border bg-white p-6 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.15)]">
            <div className="space-y-3">
              <Bubble role="user">What&rsquo;s my runway?</Bubble>
              <Bubble role="assistant">
                At your current burn of <b>CHF 6,022/mo</b> and cash of <b>CHF 108,400</b>, you have about <b>18 months</b> of runway.
              </Bubble>
              <Bubble role="user">Can I hire a senior engineer at CHF 9k?</Bubble>
              <Bubble role="assistant">
                Yes — you&rsquo;d still keep ~12 months runway. I&rsquo;d wait to close the outstanding <b>CHF 14,200</b> in invoices first.
              </Bubble>
            </div>
          </div>
        </div>
      </section>

      {/* KB-anchored capabilities strip */}
      <section id="learn" className="mx-auto max-w-7xl px-6 py-24 md:px-12">
        <div className="max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">The full loop</p>
          <h3 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
            Understand. Decide. Act. Learn.
          </h3>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
            Every screen ties back to the Mahakali Tribunal knowledge base — so answers
            aren&rsquo;t hunches, they&rsquo;re grounded. And every metric has a &ldquo;Learn this&rdquo;
            chip that opens the exact chapter behind it.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-4">
          <CapCard
            eyebrow="Understand"
            title="Cash & Liquidity"
            body="Receivables aging, DSO, DPO, rolling burn — the working-capital view your CFO would open first."
            to="/learn/receivables"
            testid="home-cap-liquidity"
          />
          <CapCard
            eyebrow="Decide"
            title="Signals & Alerts"
            body="Only what matters. Every alert scored on materiality × urgency × probability — tunable per kind."
            to="/learn/signals"
            testid="home-cap-signals"
          />
          <CapCard
            eyebrow="Act"
            title="Scenario Lab"
            body="Move a slider, watch runway re-forecast. Growth, hires, margin — the drivers a real CFO models."
            to="/learn/cash-forecast"
            testid="home-cap-scenarios"
          />
          <CapCard
            eyebrow="Learn"
            title="669-topic KB"
            body="Fundamentals, calculations, diagnostic patterns and playbooks — with an AI tutor grounded in it."
            to="/learn"
            testid="home-cap-learn"
          />
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/learn"
            data-testid="home-explore-kb"
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-medium text-background hover:bg-foreground/90"
          >
            Explore the knowledge base <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/learn/mrr"
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm hover:bg-accent"
          >
            Start with MRR
          </Link>
        </div>
      </section>

      {/* Fundraising strip */}
      <section id="decide" className="border-y border-border bg-[hsl(0_0%_98.5%)] py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 md:grid-cols-[1fr_auto] md:px-12">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Fundraising ready</p>
            <h3 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
              A pitch-ready snapshot in every workspace.
            </h3>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
              MRR, ARR, runway, growth, gross margin — plus a live cap table and dilution
              simulator. One click exports the whole thing as a board-pack PDF.
            </p>
          </div>
          <div className="rounded-[28px] border border-border bg-white p-6 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.1)]">
            <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              Readiness
            </div>
            <div className="mt-1 font-display text-5xl tracking-tight">86%</div>
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              <div>✓ MRR measurable · CHF 12,400</div>
              <div>✓ Runway ≥ 6 months · 18 mo</div>
              <div>✓ Growth positive · +14% m/m</div>
              <div className="text-amber-700">! Concentration 62% → diversify</div>
            </div>
          </div>
        </div>
      </section>
      <section id="act" className="mx-auto max-w-7xl px-6 py-28 md:px-12">
        <div className="grid grid-cols-1 items-center gap-16 md:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Pricing</p>
            <h3 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
              5 seats free.
              <br />
              <span className="italic text-foreground/60">then CHF 20 per seat.</span>
            </h3>
            <p className="mt-6 max-w-md text-muted-foreground">
              Start with your team for free. Pay only when your team grows beyond five people. No tiers. No lock-in. Every feature — including AI CFO — is free-tier included.
            </p>
          </div>
          <div className="relative overflow-hidden rounded-[32px] border border-border bg-white p-10">
            <div className="mb-6 text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Lemonade</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-7xl tracking-tight">Free</span>
              <span className="text-muted-foreground">first 5 seats</span>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              Then <b className="font-mono text-foreground">CHF 20</b> per additional seat / month
            </div>
            <ul className="mt-10 space-y-3 text-sm">
              {[
                "AI CFO — grounded in your real numbers",
                "Multi‑currency invoicing (CHF, EUR, USD, INR)",
                "Live cash‑flow forecast + Sri integration",
                "Bill scanning, vendors, budgets, tax packet",
                "Unlimited financial questions",
              ].map((b) => (
                <li key={b} className="flex items-center gap-3">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-foreground">
                    <Check className="h-3 w-3" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            <Link
              to="/signup"
              data-testid="pricing-cta-signup"
              className="mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-6 py-4 text-sm font-medium text-background hover:bg-foreground/90 active:scale-95"
            >
              Start Free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-white">        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="col-span-2">
              <Logo size={28} />
              {/* The global accounting platform for founders, freelancers, & agencies. Crafted with Beauty in Spirit. */}
              <p className="mt-4 max-w-xs text-sm text-muted-foreground">The global accounting OS for founders, freelancers &amp; agencies. Beautifully simple. Calmly powerful.</p>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">Product</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li><a href="#features" className="hover:text-foreground">Features</a></li>
                <li><a href="#ai" className="hover:text-foreground">CFO's Dashboard</a></li>
                <li><Link to="/pricing" className="hover:text-foreground">Pricing</Link></li>
                <li><Link to="/signup" className="hover:text-foreground">Start free</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">Platform</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li><Link to="/login?demo=1" className="hover:text-foreground">Live demo</Link></li>
                <li><Link to="/login" className="hover:text-foreground">Sign in</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-border pt-6 text-xs text-muted-foreground md:flex-row md:items-center">
            <div>© {new Date().getFullYear()} Mahakali Tribunal. Made for global entrepreneurs.</div>
            <div className="font-mono uppercase tracking-widest">CHF · EUR · USD · GBP · INR</div>
          </div>
        </div>
      </footer>

      {/* Verb Scroll Return chip — appears after a Verb card jump, dismisses on click or when the overview is back on screen. */}
      {showBackToOverview && (
        <button
          onClick={() => {
            const el = document.querySelector("#narrative");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            setShowBackToOverview(false);
          }}
          data-testid="verb-back-to-overview"
          className="fixed bottom-6 left-1/2 z-30 -translate-x-1/2 rounded-full border border-border bg-foreground/95 px-4 py-2.5 text-xs font-medium text-background shadow-[0_20px_40px_-20px_rgba(0,0,0,0.4)] backdrop-blur transition-all hover:bg-foreground animate-fade-up"
        >
          <span className="inline-flex items-center gap-2">
            <ArrowUp className="h-3.5 w-3.5" />
            Back to overview
          </span>
        </button>
      )}
    </div>
  );
}

function FeatureCard({ icon: Icon, eyebrow, title, desc, className = "", dark = false, iconClass = "bg-foreground text-primary" }) {
  return (
    <div
      className={`relative overflow-hidden rounded-[28px] border border-border p-8 transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-20px_rgba(0,0,0,0.12)] md:p-10 ${className}`}
    >
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl ${iconClass}`}>
        <Icon className="h-5 w-5 stroke-[1.6]" />
      </div>
      <div className={`mt-6 text-xs font-medium uppercase tracking-[0.22em] ${dark ? "text-primary" : "text-muted-foreground"}`}>{eyebrow}</div>
      <h4 className={`mt-2 font-display text-2xl tracking-tight md:text-3xl ${dark ? "text-background" : ""}`}>{title}</h4>
      <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-background/70" : "text-muted-foreground"}`}>{desc}</p>
    </div>
  );
}

function Verb({ n, verb, copy, tag, anchor }) {
  const inner = (
    <>
      <div className="text-[10px] font-mono text-muted-foreground">{n}</div>
      <h3 className="mt-2 font-display text-2xl tracking-tight">{verb}.</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{copy}</p>
      <div className="mt-4 text-[10px] font-medium uppercase tracking-[0.18em] text-foreground/60">{tag}</div>
    </>
  );
  if (anchor) {
    return (
      <a
        href={anchor}
        data-testid={`verb-${verb.toLowerCase()}`}
        onClick={(e) => {
          const el = document.querySelector(anchor);
          if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            // Brief highlight ring so visitors see where they landed.
            el.classList.add("verb-target-glow");
            setTimeout(() => el.classList.remove("verb-target-glow"), 1400);
            // Notify the page so it can show the 'Back to overview' chip.
            window.dispatchEvent(new CustomEvent("verb-jumped"));
          }
        }}
        className="group relative block rounded-[24px] border border-border bg-white p-6 transition-transform hover:-translate-y-0.5 hover:border-foreground/30"
      >
        {inner}
        <span className="absolute right-4 top-4 text-[10px] font-medium text-foreground/40 group-hover:text-foreground">↓</span>
      </a>
    );
  }
  return (
    <div className="relative rounded-[24px] border border-border bg-white p-6 transition-transform hover:-translate-y-0.5">
      {inner}
    </div>
  );
}


function Bubble({ role, children }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser ? "bg-primary text-foreground" : "bg-[hsl(0_0%_96%)] text-foreground"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function CapCard({ eyebrow, title, body, to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex flex-col rounded-[24px] border border-border bg-white p-6 transition hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-sm"
    >
      <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
        {eyebrow}
      </div>
      <div className="mt-3 font-display text-xl tracking-tight">{title}</div>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{body}</p>
      <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-foreground/80 opacity-0 transition group-hover:opacity-100">
        Open <ArrowRight className="h-3 w-3" />
      </div>
    </Link>
  );
}


function Cell({ v, primary }) {
  if (v === true) {
    return (
      <td className={`px-6 py-4 text-sm ${primary ? "font-semibold text-foreground" : "text-foreground"}`}>
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${primary ? "bg-primary text-foreground" : "bg-muted text-foreground"}`}>
          <Check className="h-3.5 w-3.5" />
        </span>
      </td>
    );
  }
  if (v === false) {
    return (
      <td className="px-6 py-4 text-sm text-muted-foreground">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted">
          <X className="h-3.5 w-3.5" />
        </span>
      </td>
    );
  }
  return <td className={`px-6 py-4 text-xs ${primary ? "font-semibold" : "text-muted-foreground"}`}>{v}</td>;
}

const CHANGELOG = [
  {
    sprint: "Sprint 6 · Sep 2026",
    label: "Sri × Lemonade",
    icon: Zap,
    items: [
      "Sri Integration API — read-only financial-context feed",
      "Bill scanning (Claude OCR → Accounts Payable)",
      "Vendors directory with recurring bill hints",
      "Accountant share link · 30-day viewer access",
    ],
  },
  {
    sprint: "Sprint 5 · Feb 2026",
    label: "Delight & commerce",
    icon: LayoutTemplate,
    items: [
      "Client portal — one link, all their invoices",
      "Budgets vs actuals with variance bars",
      "Invoice templates marketplace",
      "AI Docs Q&A (upload contract → ask)",
      "Stripe-billed subscription (CHF 20/seat, first 5 free)",
    ],
  },
  {
    sprint: "Sprint 4 · Feb 2026",
    label: "Scale the team",
    icon: ShieldCheck,
    items: [
      "Multi-user workspaces (owner/admin/member/viewer)",
      "Invoice approval workflows above threshold",
      "Public API + webhooks (HMAC signed)",
      "PWA manifest — install on mobile home screen",
    ],
  },
  {
    sprint: "Sprint 3 · Feb 2026",
    label: "AI superpowers",
    icon: Sparkles,
    items: [
      "AI CFO weekly insights email",
      "Bank-feed auto-match to invoices & expenses",
      "90-day cash-flow forecast widget",
      "Email-to-invoice parsing",
    ],
  },
];

const COMPARISON = [
  { label: "Sri × Lemonade AI CFO integration (server-to-server)", l: true, q: false, x: false, w: false },
  { label: "Multi-currency invoicing with live FX", l: true, q: true, x: true, w: "Limited" },
  { label: "AI CFO — ask anything grounded in your books", l: true, q: false, x: false, w: false },
  { label: "AI document Q&A on contracts", l: true, q: false, x: false, w: false },
  { label: "Email-to-invoice / email-to-expense", l: true, q: false, x: false, w: false },
  { label: "90-day cash-flow forecast widget", l: true, q: "Add-on", x: "Add-on", w: false },
  { label: "Bank feed auto-match", l: true, q: true, x: true, w: true },
  { label: "Team workspaces + approval workflows", l: true, q: true, x: true, w: false },
  { label: "Public API + signed webhooks", l: true, q: true, x: true, w: false },
  { label: "Swiss MwSt + EU VIES validation", l: true, q: "Partial", x: "Partial", w: false },
  { label: "Pricing per user per month", l: "5 free · CHF 20", q: "US$30+", x: "US$29+", w: "Free*" },
];
